import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Box, Card, CardContent, CardHeader, Fade, Link } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import Grid from "@mui/material/Grid";
import { useLibrarySalesAccount } from "../organizationServices";
import { isBlank, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import { useSelector } from "react-redux";
import LabeledReadOnly from "../../components/LabeledReadOnly";
import YesNoCard from "../../components/YesNoCard";
import makeStyles from "@mui/styles/makeStyles";
import ReadOnlyField from "../../components/ReadOnlyField";
import EditLibrarySalesAccountDialog from "../edit/EditLibrarySalesAccountDialog";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },

  link: {
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
}));

const LibrarySalesAccountCard = ({ id }) => {
  const auth = useSelector(state => state.auth);
  const classes = useStyles();
  const [salesAccount, setSalesAccount] = useState(null);

  const [saveAlert, setSaveAlert] = useState(false);
  const timerAutoHide = React.useRef();
  const setAutoHideTimer = useCallback(timeoutDuration => {
    clearTimeout(timerAutoHide.current);
    timerAutoHide.current = setTimeout(() => {
      setSaveAlert(false);
    }, timeoutDuration);
  }, []);

  React.useEffect(() => {
    if (saveAlert) {
      setAutoHideTimer(3000);
    }

    return () => {
      clearTimeout(timerAutoHide.current);
    };
  }, [saveAlert, setAutoHideTimer]);

  const salesAccountResponse = useLibrarySalesAccount(id);
  useEffect(() => {
    if (salesAccountResponse.isSuccess && notBlank(salesAccountResponse.data)) {
      setSalesAccount(salesAccountResponse.data);
    }
  }, [salesAccountResponse.isSuccess, salesAccountResponse.data, auth]);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const handleEditClick = useCallback(() => {
    console.log("Handling Edit click...");
    setEditDialogOpen(true);
  }, []);

  const handleEditCancel = useCallback(() => {
    setEditDialogOpen(false);
  }, []);

  const handleEditSave = useCallback(
    async data => {
      await salesAccountResponse.refetch();
      setEditDialogOpen(false);
      setSaveAlert(true);
    },
    [salesAccountResponse],
  );

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          variant: "h6",
          color: "primary",
        }}
        title="Sales Account"
        action={
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Fade in={saveAlert}>
              <Box pr={5} color="green">
                Update Successful
              </Box>
            </Fade>
            {isBlank(salesAccount) ? (
              <Tooltip title="Add Sales Account">
                <IconButton onClick={handleEditClick} size="large">
                  <AddIcon color="primary" fontSize="large" />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Edit Sales Account">
                <IconButton onClick={handleEditClick} size="large">
                  <EditIcon color="primary" fontSize="large" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {salesAccountResponse.isLoading && <PanelLoadingIndicator />}
        {salesAccountResponse.isError &&
          salesAccountResponse.error.status === 404 && (
            <Box padding={5} fontSize="1.4rem" fontStyle="italic">
              No Sales Account Found.
            </Box>
          )}
        {salesAccountResponse.isError &&
          salesAccountResponse.error.status !== 404 && (
            <Box padding={5} fontSize="1.4rem" fontStyle="italic">
              Error Loading Sales Account.
            </Box>
          )}
        {salesAccountResponse.isSuccess && notBlank(salesAccount) && (
          <Grid container spacing={3}>
            <Grid item sm={6}>
              <LabeledReadOnly label="Nimble Account Number">
                <ReadOnlyField>
                  {salesAccount.nimbleAccountNumber}
                </ReadOnlyField>
              </LabeledReadOnly>
            </Grid>

            <Grid item sm={6}>
              <LabeledReadOnly label="Population Served">
                <ReadOnlyField>{salesAccount.populationServed}</ReadOnlyField>
              </LabeledReadOnly>
            </Grid>

            <Grid item sm={9}>
              <Grid container spacing={3}>
                <Grid item sm={4}>
                  <LabeledReadOnly label="Creator?">
                    <YesNoCard value={salesAccount.creatorEnabled} />
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={4}>
                  <LabeledReadOnly label="Indie Author Project?">
                    <YesNoCard value={salesAccount.indieAuthorProjectEnabled} />
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={4}>
                  <LabeledReadOnly label="Pressbooks?">
                    <YesNoCard value={salesAccount.pressbooksEnabled} />
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={12}>
                  <LabeledReadOnly label="IAP Submission Url">
                    {notBlank(salesAccount.iapSubmissionUrl) ? (
                      <Link
                        href={salesAccount.iapSubmissionUrl}
                        target="_blank"
                        variant="h2"
                        className={classes.link}
                      >
                        {salesAccount.iapSubmissionUrl}
                      </Link>
                    ) : (
                      <ReadOnlyField>
                        {salesAccount.iapSubmissionUrl}
                      </ReadOnlyField>
                    )}
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={12}>
                  <LabeledReadOnly label="Author/Writer Url">
                    {notBlank(salesAccount.authorWriterUrl) ? (
                      <Link
                        href={salesAccount.authorWriterUrl}
                        target="_blank"
                        variant="h2"
                        className={classes.link}
                      >
                        {salesAccount.authorWriterUrl}
                      </Link>
                    ) : (
                      <ReadOnlyField>
                        {salesAccount.authorWriterUrl}
                      </ReadOnlyField>
                    )}
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={12}>
                  <LabeledReadOnly label="Pressbooks Submission Url">
                    {notBlank(salesAccount.pressbooksSubmissionUrl) ? (
                      <Link
                        href={salesAccount.pressbooksSubmissionUrl}
                        target="_blank"
                        variant="h2"
                        className={classes.link}
                      >
                        {salesAccount.pressbooksSubmissionUrl}
                      </Link>
                    ) : (
                      <ReadOnlyField>
                        {salesAccount.pressbooksSubmissionUrl}
                      </ReadOnlyField>
                    )}
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={4}>
                  <LabeledReadOnly label="Account Start Date">
                    <ReadOnlyField>
                      {salesAccount.accountStartDate}
                    </ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={4}>
                  <LabeledReadOnly label="Account Renewal Date">
                    <ReadOnlyField>
                      {salesAccount.accountRenewalDate}
                    </ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={4}>
                  <LabeledReadOnly label="CE Purchase Amount">
                    <ReadOnlyField>
                      {salesAccount.cePurchaseAmount?.toFixed(2)}
                    </ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={4}>
                  <LabeledReadOnly label="BiblioBoard Purchase Amount">
                    <ReadOnlyField>
                      {salesAccount.biblioboardPurchaseAmount?.toFixed(2)}
                    </ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={4}>
                  <LabeledReadOnly label="Creator Purchase Amount">
                    <ReadOnlyField>
                      {salesAccount.creatorPurchaseAmount?.toFixed(2)}
                    </ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={4}>
                  <LabeledReadOnly label="IAP Purchase Amount">
                    <ReadOnlyField>
                      {salesAccount.iapPurchaseAmount?.toFixed(2)}
                    </ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={4}>
                  <LabeledReadOnly label="Pressbooks Purchase Amount">
                    <ReadOnlyField>
                      {salesAccount.pressbooksPurchaseAmount?.toFixed(2)}
                    </ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>
              </Grid>
            </Grid>

            <Grid item sm={3}>
              <Grid container spacing={3}>
                <Grid item sm={12}>
                  <LabeledReadOnly label="Third Party Purchase">
                    <YesNoCard value={salesAccount.thirdPartyPurchase} />
                  </LabeledReadOnly>
                </Grid>

                {salesAccount.thirdPartyPurchase && (
                  <Fragment>
                    <Grid item sm={12}>
                      <LabeledReadOnly label="Third Party Purchase Amount">
                        <ReadOnlyField>
                          {salesAccount.thirdPartyPurchaseAmount?.toFixed(2)}
                        </ReadOnlyField>
                      </LabeledReadOnly>
                    </Grid>

                    <Grid item sm={12}>
                      <LabeledReadOnly label="Third Party Start Date">
                        <ReadOnlyField>
                          {salesAccount.thirdPartyStartDate}
                        </ReadOnlyField>
                      </LabeledReadOnly>
                    </Grid>

                    <Grid item sm={12}>
                      <LabeledReadOnly label="Third Party Renewal Date">
                        <ReadOnlyField>
                          {salesAccount.thirdPartyRenewalDate}
                        </ReadOnlyField>
                      </LabeledReadOnly>
                    </Grid>
                  </Fragment>
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
      </CardContent>
      {editDialogOpen && (
        <EditLibrarySalesAccountDialog
          open={editDialogOpen}
          organizationId={id}
          salesAccount={salesAccount}
          onCancel={handleEditCancel}
          onSave={handleEditSave}
        />
      )}
    </Card>
  );
};

export default LibrarySalesAccountCard;
